import React from 'react';
import { motion } from 'framer-motion';
import { Clock, AlertTriangle, FileX, Languages } from 'lucide-react';

const POINTS = [
  {
    icon: Clock,
    title: 'Hours, not minutes',
    body: 'Email confirmations and recorded-call transcripts get re-typed into Allegro, Endur or RightAngle. Bottlenecked by the most experienced traders on the desk.',
  },
  {
    icon: AlertTriangle,
    title: 'Errors caught too late',
    body: 'Wrong tenor, swapped counterparties, missed laycan. Every fix is a phone call, a re-amend, and a delayed settlement.',
  },
  {
    icon: Languages,
    title: 'English, Arabic, mixed threads',
    body: 'Confirmations come in mixed scripts and informal language. Most automation tools fall apart the moment a thread leaves a template.',
  },
  {
    icon: FileX,
    title: 'No audit trail of intent',
    body: 'When a trade is disputed, reconstructing what was actually agreed means hours digging through Outlook and approved messaging channels.',
  },
];

const ProblemSection = () => {
  return (
    <section className="relative py-28 md:py-32 bg-canvas-black">
      <div className="container-prose">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5 }}
            className="text-[11px] font-semibold tracking-[0.12em] uppercase text-amber-light mb-5"
          >
            The problem
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="text-display font-bold text-canvas-white text-balance"
          >
            Trades are agreed in seconds.
            <br className="hidden md:block" />{' '}
            <span className="text-ink-faint">Booking them takes the rest of the morning.</span>
          </motion.h2>
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-px bg-white/[0.06] rounded-2xl overflow-hidden border border-white/[0.06]">
          {POINTS.map(({ icon: Icon, title, body }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="bg-canvas-black p-8 md:p-10 hover:bg-canvas-surface/50 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-amber/10 ring-1 ring-amber/20 flex items-center justify-center">
                  <Icon size={16} className="text-amber" strokeWidth={1.8} />
                </div>
                <h3 className="text-[18px] font-semibold text-canvas-white tracking-tight">
                  {title}
                </h3>
              </div>
              <p className="text-[14.5px] leading-relaxed text-ink-faint">
                {body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
